import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Eye, FileText, Image as ImageIcon, CheckCircle } from 'lucide-react';
import ApiService from '../../services/api';

interface FileUploadStepProps {
  data: File[];
  onUpdate: (files: File[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  offerId: string;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({ data, onUpdate, onNext, onPrevious, offerId }) => {
  const [files, setFiles] = useState<File[]>(data || []);
  const [previews, setPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = [...files, ...acceptedFiles];
    setFiles(newFiles);
    onUpdate(newFiles);

    // Generate previews
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviews(prev => [...prev, e.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });
  }, [files, onUpdate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/pdf': ['.pdf']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onUpdate(newFiles);
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canProceed = files.length > 0;

  const handleUpload = async (filesToUpload: File[]) => {
    setLoading(true);
    setError(null);
    try {
      // إرسال الملفات للـ API مع offer_id
      await ApiService.uploadOfferFiles(offerId, filesToUpload);
      onUpdate(filesToUpload);
      onNext();
    } catch (e) {
      setError('حدث خطأ أثناء رفع الملفات');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-8"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            رفع ملفات الفلاير
          </h2>
          <p className="text-gray-600">
            ارفع صور أو ملفات PDF للعرض (الحد الأقصى: 50 ميجابايت لكل ملف)
          </p>
        </div>

        {/* Drop Zone */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
            ${isDragActive 
              ? 'border-primary-500 bg-primary-50' 
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
            }
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center space-y-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isDragActive ? 'bg-primary-100' : 'bg-gray-100'
            }`}>
              <Upload className={`w-8 h-8 ${
                isDragActive ? 'text-primary-600' : 'text-gray-500'
              }`} />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                {isDragActive ? 'اسحب الملفات هنا' : 'اسحب الملفات هنا أو انقر للاختيار'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                يدعم JPG, PNG, PDF حتى 50MB
              </p>
            </div>
          </div>
        </div>

        {/* Files List */}
        {files.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              الملفات المرفوعة ({files.length})
            </h3>
            <div className="space-y-4">
              {files.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center space-x-4 space-x-reverse p-4 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    {file.type.startsWith('image/') ? (
                      <ImageIcon className="w-6 h-6 text-gray-500" />
                    ) : (
                      <FileText className="w-6 h-6 text-gray-500" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{file.name}</h4>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <CheckCircle className="w-5 h-5 text-success-500" />
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Grid */}
        {previews.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">معاينة الصور</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {previews.map((preview, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="aspect-square bg-gray-100 rounded-lg overflow-hidden group cursor-pointer hover:shadow-md transition-shadow"
                >
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-200 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Tips */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">نصائح لرفع أفضل:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• استخدم صور عالية الجودة لضمان دقة أفضل في الكشف</li>
            <li>• تأكد من وضوح النصوص والأسعار في الصور</li>
            <li>• يمكنك رفع ملفات PDF متعددة الصفحات</li>
            <li>• ترتيب الملفات سيحدد ترتيب المعالجة</li>
            <li>• الحد الأقصى لحجم الملف: 50 ميجابايت</li>
            <li>• التنسيقات المدعومة: JPG, PNG, PDF</li>
          </ul>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-8">
          <button
            onClick={onPrevious}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="text-sm text-gray-500">
            {files.length} ملف مرفوع
          </div>
          
          <button
            onClick={() => handleUpload(files)}
            disabled={loading}
            className={`btn-primary ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {loading ? 'جاري الرفع...' : 'التالي'}
          </button>
          {error && <div className="text-red-600 mt-2">{error}</div>}
        </div>
      </div>
    </motion.div>
  );
};

export default FileUploadStep;